import { useEffect, useMemo, useState } from 'react';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Field from '../../components/common/Field';
import Avatar from '../../components/common/Avatar';
import EmptyState from '../../components/common/EmptyState';
import LoadingRow from '../../components/common/LoadingRow';
import Spinner from '../../components/common/Spinner';
import useTodosStore, { selectGroups, GENERAL } from '../../store/useTodosStore';
import useEventsStore from '../../store/useEventsStore';
import useMyProfileStore from '../../store/useMyProfileStore';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import roomParentsService from '../../api/services/roomParents.service';
import { showToast } from '../../store/useToastStore';
import { copyLink } from '../../utils/clipboard';
import { todoChecklistLinkUrl } from '../../utils/portalLinks';
import { getStoredUser, getTokenSubject } from '../../utils/token';
import { avatarFor } from '../../utils/avatar';
import { fmtDate, fmtMonthDay } from '../../utils/formatters';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { IconEdit, IconLink, IconPlus, IconTrash } from '../../components/icons';

/* Room Parent To-Do — the class checklist from /roomParentAdmin/todos, grouped
   by the event each task belongs to (no event = General). */

const blankDraft = (groupId = '') => ({ id: null, eventId: groupId, task: '', owner: '', due: '' });

export default function TodosPage() {
  const ctx = useContextStore(selectCurrentContext);
  const networkId = ctx?.id || null;

  const groups = useTodosStore(selectGroups);
  const { loading, todosNetworkId, load, addTodo, editTodo, toggleTodo, deleteTodo } = useTodosStore();
  /* the event picker offers every event on the class, not just the ones that
     already carry a task */
  const { events, loadEvents } = useEventsStore();
  const me = useMyProfileStore((s) => s.me);

  const [members, setMembers] = useState(null);
  const [draft, setDraft] = useState(null);
  const [quickGroup, setQuickGroup] = useState(null);
  const [quickTask, setQuickTask] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  /* every write needs an assignee, and a quick add assigns it to you */
  const myUserId = useMemo(() => {
    const stored = getStoredUser();
    return (
      me?.id || me?.user_id || me?.userId || me?.sub ||
      stored?.user_id || stored?.userId || stored?.sub ||
      getTokenSubject() || ''
    );
  }, [me]);

  /* force: this is the page that edits the list, so it always opens on what the
     server has rather than whatever the dashboard card cached earlier */
  useEffect(() => {
    if (!networkId) return;
    load(networkId, { force: true }).catch((err) => showToast(err.message));
    loadEvents(networkId, { page: 1, limit: 100 }, 'class').catch(() => {});
  }, [networkId, load, loadEvents]);

  /* assignee options — the class roster, same source as the Parents page */
  useEffect(() => {
    setMembers(null);
    if (!networkId) return undefined;
    let cancelled = false;
    roomParentsService
      .getGroupMembers(networkId, { type: 'group', page: 1, limit: 100 })
      .then((r) => { if (!cancelled) setMembers(r.members); })
      .catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [networkId]);

  /* the API builds an assignee's name from first/last name, which can come back
     empty — fall back to the roster's copy */
  const nameFor = (person) =>
    person.name || (members || []).find((m) => m.id === person.id)?.name || 'Unassigned';

  const classEvents = events.filter((e) => e.scope === 'class' && e.eventId);
  const groupOptions = [{ id: '', name: GENERAL }, ...classEvents.map((e) => ({ id: e.eventId, name: e.title }))];

  /* A group's heading: what the API resolved, then this class's own event list,
     and only then a generic label — so a task never loses its event. */
  const eventNameById = useMemo(
    () => Object.fromEntries(classEvents.map((e) => [e.eventId, e.title])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events]
  );
  const groupLabel = (g) => (g.id ? g.name || eventNameById[g.id] || 'Event' : GENERAL);

  const openNew = (groupId = '') => {
    setQuickGroup(null);
    setDraft({ ...blankDraft(groupId), owner: myUserId });
  };

  const openEdit = (task) => {
    setQuickGroup(null);
    setDraft({
      id: task.id,
      eventId: task.groupId,
      task: task.task,
      owner: task.assignedTo[0] || myUserId,
      due: task.due,
    });
  };

  const saveDraft = async () => {
    const task = draft.task.trim();
    if (!task) { window.alert('Give the task a name.'); return; }
    if (!draft.owner) { window.alert('Pick who the task is for.'); return; }
    setSaving(true);
    try {
      if (draft.id) {
        await editTodo(networkId, draft.id, {
          task,
          assignedTo: [draft.owner],
          /* null clears the date; '' moves the task back to General */
          dueDate: draft.due || null,
          eventId: draft.eventId || '',
        });
        showToast('Task updated ✓');
      } else {
        await addTodo(networkId, {
          task,
          assignedTo: [draft.owner],
          dueDate: draft.due || undefined,
          eventId: draft.eventId || undefined,
        });
        showToast('Task added ✓');
      }
      setDraft(null);
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveQuick = async (groupId) => {
    const task = quickTask.trim();
    if (!task) { setQuickGroup(null); return; }
    if (!myUserId) { showToast('Could not resolve your user id — please log in again.'); return; }
    setSaving(true);
    try {
      await addTodo(networkId, { task, assignedTo: [myUserId], eventId: groupId || undefined });
      setQuickTask('');
      setQuickGroup(null);
      showToast('Task added ✓');
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* The API only lets an assignee or the task's creator tick it off — anyone
     else gets a 403, so the box is disabled for them rather than failing. */
  const canMark = (task) =>
    !!myUserId && ((task.assignedTo || []).includes(myUserId) || task.createdBy === myUserId);

  const toggle = async (task) => {
    setBusyId(task.id);
    try {
      const result = await toggleTodo(networkId, task);
      /* Reopening removes your own mark only. If someone else has also ticked
         the task off it stays done, and the box springs back — say why. */
      if (task.done && result?.done) {
        showToast(`Still marked done by ${result.doneCount} other room parent${result.doneCount === 1 ? '' : 's'}.`);
      }
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusyId(null);
    }
  };

  /* Delete = custom confirm, then the API; the row goes once the server agrees. */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    const task = deleteTarget;
    if (!task) return;
    setDeleting(true);
    try {
      await deleteTodo(networkId, task.id);
      /* don't leave the edit form open on a task that no longer exists */
      if (draft?.id === task.id) setDraft(null);
      setDeleteTarget(null);
      showToast('Task deleted ✓');
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* the public checklist lives on roomparent.com, keyed by this class's network id */
  const shareChecklist = () => {
    const url = todoChecklistLinkUrl(networkId);
    if (!url) { showToast('Pick a class first to share its checklist.'); return; }
    copyLink(url);
  };

  const pending = todosNetworkId !== networkId;

  return (
    <div className="sec on">
      <SectionHead
        title="Room Parent To-Do"
        sub="Split the work with your co-room-parent — assign tasks, set due dates, check them off together."
      />

      <Card
        title="Tasks"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={shareChecklist}>
              <IconLink />
              Share Checklist
            </button>
            <button className="btn btn-primary" onClick={() => openNew('')}>+ Add Task</button>
          </div>
        }
      >
        <div className={`inline-form ${draft ? 'on' : ''}`}>
          {draft && (
            <>
              <div className="form-grid">
                <Field label="Task" style={{ gridColumn: '1/-1' }}>
                  <input
                    type="text"
                    value={draft.task}
                    placeholder="What needs doing?"
                    autoFocus
                    onChange={(e) => setDraft({ ...draft, task: e.target.value })}
                  />
                </Field>
                <Field label="Assigned to">
                  <select value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })}>
                    <option value="">— Pick someone —</option>
                    {(members || []).map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Due date (optional)">
                  <input type="date" value={draft.due} onChange={(e) => setDraft({ ...draft, due: e.target.value })} />
                </Field>
                <Field label="Group">
                  <select value={draft.eventId} onChange={(e) => setDraft({ ...draft, eventId: e.target.value })}>
                    {groupOptions.map((g) => <option key={g.id || 'general'} value={g.id}>{g.name}</option>)}
                  </select>
                </Field>
              </div>
              <div className="inline-form-actions">
                <button className="btn btn-ghost" disabled={saving} onClick={() => setDraft(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={saving} onClick={saveDraft}>
                  {saving ? <><Spinner /> Saving…</> : draft.id ? 'Save Task' : 'Add Task'}
                </button>
              </div>
            </>
          )}
        </div>

        {pending || (loading && !groups.length) ? (
          <LoadingRow>Loading tasks…</LoadingRow>
        ) : !groups.length ? (
          <EmptyState>Nothing on the list yet — add the first task.</EmptyState>
        ) : (
          groups.map((g) => (
            <div className="todo-group" key={g.id || 'general'}>
              <h4>{groupLabel(g)}</h4>
              <div className="todo-group-list">
                {g.tasks.map((t) => {
                  const owner = t.owners[0] || null;
                  const ownerName = owner ? nameFor(owner) : 'Unassigned';
                  const av = avatarFor(ownerName, []);
                  const others = t.owners.length - 1;
                  const mine = canMark(t);
                  const doneByNames = (t.doneBy || []).map((p) => p.name).join(', ');
                  return (
                    <div className={`task-row ${t.done ? 'done' : ''}`} key={t.id}>
                      <label className="task-t">
                        <input
                          type="checkbox"
                          className="task-check"
                          checked={t.done}
                          disabled={busyId === t.id || !mine}
                          title={
                            !mine
                              ? 'Only the people this task is assigned to can tick it off'
                              : doneByNames
                                ? `Marked done by ${doneByNames}`
                                : undefined
                          }
                          onChange={() => toggle(t)}
                        />
                        <span>{t.task}</span>
                      </label>
                      {/* task-meta collapses on desktop (display:contents), so who
                          and due stay their own grid columns there and share one
                          line on a phone */}
                      <div className="task-meta">
                        <div className="task-who">
                          <Avatar src={owner?.picture} initials={av.initials} color={av.color} size={22} fontSize={8.5} />
                          <span className="task-who-name">{ownerName}{others > 0 ? ` +${others}` : ''}</span>
                        </div>
                        <div className="task-due">
                          {t.due ? (
                            <>
                              <span className="due-long">Due {fmtDate(t.due)}</span>
                              {/* phones drop the year — it is always the current one */}
                              <span className="due-short">Due {fmtMonthDay(t.due).mo} {fmtMonthDay(t.due).dy}</span>
                            </>
                          ) : (
                            'No due date'
                          )}
                        </div>
                      </div>
                      {/* nothing to edit or delete on a finished task — untick it first */}
                      <div className="task-acts">
                        {!t.done && (
                          <>
                            <button className="icon-btn" title="Edit task" onClick={() => openEdit(t)}>
                              <IconEdit />
                            </button>
                            <button className="icon-btn danger" title="Delete task" onClick={() => setDeleteTarget(t)}>
                              <IconTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {quickGroup === g.id ? (
                <div className="task-quick">
                  <input
                    type="text"
                    value={quickTask}
                    placeholder={`Add a task to ${groupLabel(g)}`}
                    autoFocus
                    onChange={(e) => setQuickTask(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveQuick(g.id);
                      if (e.key === 'Escape') { setQuickTask(''); setQuickGroup(null); }
                    }}
                  />
                  <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => saveQuick(g.id)}>Add</button>
                  <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => { setQuickTask(''); setQuickGroup(null); }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div
                  className="task-add"
                  onClick={() => { setDraft(null); setQuickTask(''); setQuickGroup(g.id); }}
                >
                  <IconPlus />
                  Add task
                </div>
              )}
            </div>
          ))
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Task"
        confirmLabel="Delete Task"
        busy={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      >
        {deleteTarget && (
          <>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
              {deleteTarget.task}
            </div>
            <div className="hint" style={{ marginBottom: 12 }}>
              {deleteTarget.owners[0] ? nameFor(deleteTarget.owners[0]) : 'Unassigned'}
              {deleteTarget.due ? ` · Due ${fmtDate(deleteTarget.due)}` : ''}
            </div>
            <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
              It disappears for your co-room-parent too. This can't be undone.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
